export const CuringMethod=Object.freeze({
  DRY:'dry',
  WET_BRINE:'wet_brine',
  EQUILIBRIUM:'equilibrium',
  INJECTION:'injection',
  OTHER:'other'
});

const CURING_METHODS=new Set(Object.values(CuringMethod));
// Canonical duration storage uses whole minutes so future recipe conversion and
// locale presentation do not depend on device-specific time units.
const MAX_DURATION_MINUTES=90*24*60;
const MAX_NOTES=4000;

function requiredId(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(`${label} is required`);
  if(text.length>128)throw new TypeError(`${label} is too long`);
  return text;
}

function optionalText(value,label,max=MAX_NOTES){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>max)throw new TypeError(`${label} is too long`);
  return text;
}

function optionalDuration(value,label){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isInteger(number)||number<1||number>MAX_DURATION_MINUTES){
    throw new TypeError(`${label} must be a positive whole number of minutes`);
  }
  return number;
}

function optionalCuringMethod(value){
  if(value==null||value==='')return null;
  const key=String(value).trim().toLowerCase();
  if(!CURING_METHODS.has(key))throw new TypeError('Unsupported curing method');
  return key;
}

export function createItemPreparation({
  itemId,
  userId,
  curingMethodKey=null,
  curingMethodCustom=null,
  curingDurationMinutes=null,
  curingNotes=null,
  marinadeNotes=null,
  spicesNotes=null,
  dryingDurationMinutes=null,
  dryingNotes=null,
  preparationDurationMinutes=null,
  preparationNotes=null
}={}){
  const method=optionalCuringMethod(curingMethodKey);
  const custom=optionalText(curingMethodCustom,'curingMethodCustom',160);

  if(method===CuringMethod.OTHER&&!custom){
    throw new TypeError('curingMethodCustom is required for other curing method');
  }
  if(method!==CuringMethod.OTHER&&custom){
    throw new TypeError('curingMethodCustom is only allowed for other curing method');
  }

  return Object.freeze({
    itemId:requiredId(itemId,'itemId'),
    userId:requiredId(userId,'userId'),
    curingMethodKey:method,
    curingMethodCustom:custom,
    curingDurationMinutes:optionalDuration(curingDurationMinutes,'curingDurationMinutes'),
    curingNotes:optionalText(curingNotes,'curingNotes'),
    marinadeNotes:optionalText(marinadeNotes,'marinadeNotes'),
    spicesNotes:optionalText(spicesNotes,'spicesNotes'),
    dryingDurationMinutes:optionalDuration(dryingDurationMinutes,'dryingDurationMinutes'),
    dryingNotes:optionalText(dryingNotes,'dryingNotes'),
    preparationDurationMinutes:optionalDuration(preparationDurationMinutes,'preparationDurationMinutes'),
    preparationNotes:optionalText(preparationNotes,'preparationNotes')
  });
}

export function updateItemPreparation(preparation,changes={}){
  const current=createItemPreparation(preparation);
  return createItemPreparation({
    ...current,
    ...changes,
    itemId:current.itemId,
    userId:current.userId
  });
}

export function itemPreparationToRow(preparation){
  const value=createItemPreparation(preparation);
  return Object.freeze({
    item_id:value.itemId,
    user_id:value.userId,
    curing_method_key:value.curingMethodKey,
    curing_method_custom:value.curingMethodCustom,
    curing_duration_minutes:value.curingDurationMinutes,
    curing_notes:value.curingNotes,
    marinade_notes:value.marinadeNotes,
    spices_notes:value.spicesNotes,
    drying_duration_minutes:value.dryingDurationMinutes,
    drying_notes:value.dryingNotes,
    preparation_duration_minutes:value.preparationDurationMinutes,
    preparation_notes:value.preparationNotes
  });
}

export function itemPreparationFromRow(row={}){
  return createItemPreparation({
    itemId:row.item_id,
    userId:row.user_id,
    curingMethodKey:row.curing_method_key,
    curingMethodCustom:row.curing_method_custom,
    curingDurationMinutes:row.curing_duration_minutes,
    curingNotes:row.curing_notes,
    marinadeNotes:row.marinade_notes,
    spicesNotes:row.spices_notes,
    dryingDurationMinutes:row.drying_duration_minutes,
    dryingNotes:row.drying_notes,
    preparationDurationMinutes:row.preparation_duration_minutes,
    preparationNotes:row.preparation_notes
  });
}
